import { computed, inject, Injectable, signal } from '@angular/core';

import { RetailCrmService } from '../customer/clients/retail-crm/retail-crm.service';
import { CustomerAddress } from '../customer/model/customer-information.model';
import { Customer, makeBlankCustomer } from '../customer/model/customer.model';
import { FoundCustomer } from '../customer/model/found-customer.model';
import { CustomerValidationService } from '../customer/services/customer-validation.service';
import { SearchCustomerRequestType, SearchService } from '../customer/services/search.service';
import { blankRequestTrackerView } from '../utils/requests/request-tracker';

export enum SearchOrigin {
  CUSTOMER_DATA = 'CUSTOMER_DATA',
  VEHICLE = 'VEHICLE',
}

/**
 * This service is responsible for handling the state of the customer search.
 *
 * It uses the {@link SearchService} to perform requests against Retail CRM and the NSC Database (GCDM).
 * The VIN/licence search additionally runs against ESO CRM.
 */
@Injectable({
  providedIn: 'root',
})
export class SearchFormService {
  private readonly searchService = inject(SearchService);

  private readonly retailCrmService = inject(RetailCrmService);
  private readonly customerValidationService = inject(CustomerValidationService);

  readonly firstName = signal<string | undefined>(undefined);
  readonly lastName = signal<string | undefined>(undefined);
  readonly suffix = signal<string | undefined>(undefined);
  readonly bmwMiniId = signal<string | undefined>(undefined);
  readonly mobilePhone = signal<string | undefined>(undefined);
  readonly phone = signal<string | undefined>(undefined);
  readonly taxNumber = signal<string | undefined>(undefined);

  readonly vin = signal<string | undefined>(undefined);
  readonly licenceNumber = signal<string | undefined>(undefined);

  readonly address = signal<CustomerAddress>({});

  readonly searchResults = signal<FoundCustomer[] | undefined>(undefined);

  readonly attemptedRequests = computed(() => this.requestTracker().attemptedRequests());

  private readonly requestTracker = signal(blankRequestTrackerView<SearchCustomerRequestType>());
  readonly requestsInProgress = computed(() => this.requestTracker().requestsInProgress());
  readonly anyRequestsInProgress = computed(() => this.requestsInProgress().size > 0);
  readonly failedRequests = computed(() => this.requestTracker().failedRequests());
  readonly requestWarnings = computed(() => this.requestTracker().requestWarnings());

  readonly isMobilePhoneValid = computed(() =>
    this.customerValidationService.isPhoneNumberValidWithoutCountry(this.mobilePhone()),
  );
  readonly isPhoneValid = computed(() => this.customerValidationService.isPhoneNumberValidWithoutCountry(this.phone()));

  /** Tracks what kind of search produced the current `searchResults`. */
  private readonly searchOrigin = signal<SearchOrigin | undefined>(undefined);
  /** True when the current results come from a VIN / licence-plate search. */
  readonly isVehicleSearch = computed(() => this.searchOrigin() === SearchOrigin.VEHICLE);
  /** True when the vehicle search runs against Retail CRM alone (CUSTAPP-607). */
  readonly vehicleSearchIsRetailOnly = computed(() => !this.searchService.vehicleSearchIncludesEsoCrm());

  readonly isAMainDataFieldFilled = computed(
    () =>
      !!(
        this.firstName() ||
        this.lastName() ||
        this.bmwMiniId() ||
        this.phone() ||
        this.mobilePhone() ||
        this.taxNumber()
      ),
  );

  readonly isAnyDataFieldFilled = computed(
    () =>
      !!(
        this.isAMainDataFieldFilled() ||
        this.suffix() ||
        this.address().postalCode ||
        this.address().street ||
        this.address().city ||
        this.address().houseNumber
      ),
  );

  resetForm() {
    this.firstName.set(undefined);
    this.lastName.set(undefined);
    this.suffix.set(undefined);
    this.bmwMiniId.set(undefined);
    this.mobilePhone.set(undefined);
    this.phone.set(undefined);
    this.taxNumber.set(undefined);
    this.address.set({});
    this.searchResults.set(undefined);
    this.requestTracker.set(blankRequestTrackerView<SearchCustomerRequestType>());
    this.vin.set(undefined);
    this.licenceNumber.set(undefined);
    this.searchOrigin.set(undefined);
  }

  readonly getSearchParameterAsCustomer = computed<Customer>(() => ({
    ...makeBlankCustomer(),
    firstName: this.firstName(),
    lastName: this.lastName(),
    suffix: this.suffix(),
    privateContact: {
      email: this.bmwMiniId(),
      mobile: this.mobilePhone(),
      phone: this.phone(),
      address: this.address(),
    },
    taxNumber: this.taxNumber(),
  }));

  readonly isAdvancedSearchExpanded = signal<boolean>(false);

  async searchCustomers() {
    this.searchOrigin.set(SearchOrigin.CUSTOMER_DATA);
    const { tracker, resultPromise: customersPromise } = this.searchService.searchCustomersWithTracker({
      firstName: this.firstName(),
      lastName: this.lastName(),
      suffix: this.suffix(),
      email: this.bmwMiniId(),
      phone: this.customerValidationService.normalizePhoneNumber(this.phone()),
      mobilePhone: this.customerValidationService.normalizePhoneNumber(this.mobilePhone()),
      postalCode: this.address()?.postalCode,
      street: this.address()?.street,
      city: this.address()?.city,
      houseNumber: this.address()?.houseNumber,
      taxNumber: this.taxNumber(),
    });
    this.requestTracker.set(tracker);
    this.searchResults.set((await customersPromise) ?? []);
  }

  expandAdvancedSearch(expand: boolean) {
    this.isAdvancedSearchExpanded.update(() => expand);
  }

  async fetchAccountNumberWithImport(gcid: string): Promise<string> {
    return (await this.fetchAccountNumberWithoutImport(gcid)) || (await this.getAccountNumberViaImportToRetail(gcid));
  }

  async fetchAccountNumberWithoutImport(gcid: string): Promise<string | undefined> {
    const customerInRetail = await this.retailCrmService.searchAccounts({ gcid });

    if (customerInRetail.length === 1) {
      const accountNumber = customerInRetail[0].accountNumber;
      if (!accountNumber) {
        throw new Error('#CDFTF0087 Account number of customer in Retail CRM is undefined.');
      }
      return accountNumber;
    } else if (customerInRetail.length === 0) {
      return undefined;
    } else {
      throw new Error('#CDFTF0088 Found multiple customer in Retail CRM for the same GCID.');
    }
  }

  private async getAccountNumberViaImportToRetail(gcid: string) {
    const accountNumber = await this.retailCrmService.importFromGcdm(gcid);
    if (!accountNumber) {
      throw new Error(
        `#CDFTF0089 Could not fetch account number of customer via import to Retail CRM using GCID '${gcid}'.`,
      );
    }
    return accountNumber;
  }

  async searchVIN() {
    const vin = this.vin();
    if (!vin) {
      throw new Error('#CDFTF0132 Unable to search using VIN, as no VIN has been entered.');
    }
    this.searchOrigin.set(SearchOrigin.VEHICLE);
    const { tracker, resultPromise: customersPromise } = this.searchService.searchVinOrLicenceWithTracker({
      vin,
      licenceNumber: undefined,
    });
    this.requestTracker.set(tracker);
    this.searchResults.set((await customersPromise) ?? []);
  }

  async searchLicence() {
    const licenceNumber = this.licenceNumber();
    if (!licenceNumber) {
      throw new Error('#CDFTF0133 Unable to search using licence number, as no licence number has been entered.');
    }
    this.searchOrigin.set(SearchOrigin.VEHICLE);
    const { tracker, resultPromise: customersPromise } = this.searchService.searchVinOrLicenceWithTracker({
      vin: undefined,
      licenceNumber,
    });
    this.requestTracker.set(tracker);
    this.searchResults.set((await customersPromise) ?? []);
  }
}
